// Runtime for interaktive fortællinger (Fortælling-ekstraøvelse).

import { STORY_SCRIPTS, getStoryScript, type StoryScript } from '../data/stories';
import type { StoryBeat, StoryBlankSpec } from '../data/stories/types';
import type { UserProfile } from '../types';
import {
  lookupNounMorphDa,
  resolveDefiniteFormDa,
  resolveNounMorphDa,
  type IndefiniteArticle,
} from './corMorphology';
import { getCumulativeUnlockedKeys, textUsesOnlyKeys } from './dictationUnits';
import { pronounInsertionText } from './nameGender';

const SLOT_PLACEHOLDER_RE = /\{\{(\w+)(?::(\d+))?\}\}/g;
const ART_PLACEHOLDER_RE = /\{\{art:(\w+)\}\}/g;

/** COR-bøjning knyttet til et slot (fx ting). */
export interface StorySlotMorph {
  article: IndefiniteArticle;
  definite: string;
}

function morphForSlot(
  slotId: string,
  slotValues: Record<string, string>,
  slotMorph?: Record<string, StorySlotMorph>,
): StorySlotMorph {
  const cached = slotMorph?.[slotId];
  if (cached) return cached;
  const raw = slotValues[slotId]?.trim() ?? '';
  const resolved = resolveNounMorphDa(raw);
  return {
    article: resolved.indefiniteArticle,
    definite: resolved.definiteSingular,
  };
}

/** Segment i én beat: fast tekst (prikker) eller frit hul. */
export type StorySegment =
  | { kind: 'literal'; text: string }
  | { kind: 'blank'; id: string; maxLength: number };

/** @deprecated Brug parseBeatSegments */
export type StoryStep =
  | { kind: 'narrate'; text: string }
  | { kind: 'blank'; id: string; maxLength: number };

/**
 * Værdi for {{id}} / {{id:N}}.
 * Bestemt form kun ved genbrug ({{id}} uden :tal) og definiteOnReuse.
 */
export function slotInsertionText(
  id: string,
  slotValues: Record<string, string>,
  script: StoryScript,
  slotMorph?: Record<string, StorySlotMorph>,
  isReuse = false,
): string {
  const raw = slotValues[id] ?? '';
  if (!isReuse || !script.blanks?.[id]?.definiteOnReuse) return raw;
  return morphForSlot(id, slotValues, slotMorph).definite;
}

/** Fjern overflødigt mellemrum i literal-segmenter. */
export function finalizeLiteralText(
  text: string,
  trimLeading = false,
): string {
  let t = trimLeading ? text.trimStart() : text;
  t = t.replace(/  +/g, ' ');
  return t;
}

/** Ubestemt artikel (en/et) for {{art:id}}. */
export function articleInsertionText(
  forSlotId: string,
  slotValues: Record<string, string>,
  slotMorph?: Record<string, StorySlotMorph>,
): string {
  if (!(forSlotId in slotValues)) return 'en';
  return morphForSlot(forSlotId, slotValues, slotMorph).article;
}

/** Alle pladsholdere i rækkefølge (art, pron, slot). */
function* templateTokens(
  template: string,
): Generator<
  | { kind: 'art'; forSlotId: string; index: number; length: number }
  | { kind: 'pron'; forSlotId: string; index: number; length: number }
  | { kind: 'slot'; id: string; maxLen: string | undefined; index: number; length: number }
> {
  const combined =
    /\{\{art:(\w+)\}\}|\{\{pron:(\w+)\}\}|\{\{(\w+)(?::(\d+))?\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = combined.exec(template)) !== null) {
    if (m[1] !== undefined) {
      yield {
        kind: 'art',
        forSlotId: m[1],
        index: m.index,
        length: m[0].length,
      };
    } else if (m[2] !== undefined) {
      yield {
        kind: 'pron',
        forSlotId: m[2],
        index: m.index,
        length: m[0].length,
      };
    } else {
      yield {
        kind: 'slot',
        id: m[3]!,
        maxLen: m[4],
        index: m.index,
        length: m[0].length,
      };
    }
  }
}

export interface ParseBeatSegmentsOptions {
  /**
   * Bevar segmenter omkring udfyldte huller (tekst før/efter hullet forbliver
   * separate skridt). Bruges i Fortælling 2 så eleven stadig skriver tekst efter hullet.
   */
  keepSegmentBoundaries?: boolean;
}

export function parseBeatSegments(
  template: string,
  slotValues: Record<string, string>,
  script: StoryScript,
  slotMorph?: Record<string, StorySlotMorph>,
  opts?: ParseBeatSegmentsOptions,
): StorySegment[] {
  const keepBoundaries = opts?.keepSegmentBoundaries === true;
  const defaultMax = script.defaultBlankLength ?? 12;
  const segments: StorySegment[] = [];
  let buffer = '';
  let last = 0;
  let trimLeadingNextLiteral = false;

  const pushLiteral = (raw: string) => {
    const parts = raw.split('\n');
    for (let i = 0; i < parts.length; i++) {
      const text = finalizeLiteralText(
        parts[i]!,
        trimLeadingNextLiteral && i === 0,
      );
      if (text.length > 0) {
        segments.push({ kind: 'literal', text });
      }
    }
    trimLeadingNextLiteral = false;
  };

  for (const token of templateTokens(template)) {
    buffer += template.slice(last, token.index);
    if (token.kind === 'art') {
      buffer += articleInsertionText(token.forSlotId, slotValues, slotMorph);
    } else if (token.kind === 'pron') {
      buffer += pronounInsertionText(token.forSlotId, slotValues);
    } else {
      const id = token.id;
      const maxLength =
        token.maxLen !== undefined
          ? parseInt(token.maxLen, 10)
          : (script.blanks?.[id]?.maxLength ?? defaultMax);
      const isReuse = token.maxLen === undefined;

      if (id in slotValues) {
        if (keepBoundaries) {
          if (buffer.length > 0) pushLiteral(buffer);
          buffer = '';
          segments.push({ kind: 'blank', id, maxLength });
          trimLeadingNextLiteral = true;
        } else {
          buffer += slotInsertionText(
            id,
            slotValues,
            script,
            slotMorph,
            isReuse,
          );
        }
      } else {
        if (buffer.length > 0) pushLiteral(buffer);
        buffer = '';
        segments.push({ kind: 'blank', id, maxLength });
        trimLeadingNextLiteral = true;
      }
    }
    last = token.index + token.length;
  }

  buffer += template.slice(last);
  if (buffer.length > 0) pushLiteral(buffer);

  return segments;
}

/** @deprecated Brug parseBeatSegments */
export function parseBeatSteps(
  template: string,
  slotValues: Record<string, string>,
  script: StoryScript,
  slotMorph?: Record<string, StorySlotMorph>,
): StoryStep[] {
  return parseBeatSegments(template, slotValues, script, slotMorph).map((s) =>
      s.kind === 'literal'
        ? { kind: 'narrate' as const, text: s.text }
        : { kind: 'blank' as const, id: s.id, maxLength: s.maxLength },
    );
}

/** TTS for fast tekst før et hul (uden brugerord). */
export function literalSpeechText(literal: string): string {
  return literal.trim();
}

/** @deprecated Brug resolveDefiniteFormDa fra corMorphology. */
export function definiteFormDa(word: string): string {
  return resolveDefiniteFormDa(word);
}

/** Ord til oplæsning fra speechFromSlot-huller (ældre skabeloner). */
export function speechWordFromBlankSpec(
  fromSlotId: string,
  blankSpec: StoryBlankSpec,
  slotValues: Record<string, string>,
  script: StoryScript,
  slotMorph?: Record<string, StorySlotMorph>,
): string | undefined {
  const raw = slotValues[fromSlotId]?.trim();
  if (!raw) return undefined;
  const sourceSpec = script.blanks?.[fromSlotId];
  if (blankSpec.speechDefinite || sourceSpec?.definiteOnReuse) {
    return morphForSlot(fromSlotId, slotValues, slotMorph).definite;
  }
  return raw;
}

/**
 * TTS for et literal-segment — kobler til næste hul og tidligere variabler.
 * Fx «sagde » + ting → «sagde uglen» før brugeren skriver i tingDef.
 */
export function segmentLiteralSpeech(
  literalText: string,
  segmentIndex: number,
  segments: StorySegment[],
  slotValues: Record<string, string>,
  script: StoryScript,
  slotMorph?: Record<string, StorySlotMorph>,
): string {
  const trimmed = literalText.trim();
  const next = segments[segmentIndex + 1];

  if (next?.kind === 'blank') {
    const spec = script.blanks?.[next.id];
    if (spec?.speechFromSlot) {
      const word = speechWordFromBlankSpec(
        spec.speechFromSlot,
        spec,
        slotValues,
        script,
        slotMorph,
      );
      if (word) {
        if (/^sagde\s*$/i.test(trimmed)) return `sagde ${word}`;
        if (trimmed.toLowerCase().startsWith('sagde ')) {
          return `sagde ${word}`;
        }
      }
    }

    const prev = segments[segmentIndex - 1];
    if (prev?.kind === 'blank' && slotValues[prev.id]) {
      return narrateSpeechText(
        literalText,
        slotValues,
        script,
        prev.id,
        slotMorph,
      ).trim();
    }
  }

  return literalSpeechText(literalText);
}

/** Hint til «Vis ord» på frie huller. */
export function getBlankHint(
  script: StoryScript,
  blankId: string,
): string | undefined {
  return script.blanks?.[blankId]?.hint;
}

/** Slot-id'er der skal bøjes i bestemt form ved genbrug (COR). */
export function slotIdsWithDefiniteReuse(script: StoryScript): string[] {
  if (!script.blanks) return [];
  return Object.entries(script.blanks)
    .filter(([, spec]) => spec.definiteOnReuse)
    .map(([id]) => id);
}

/** Hent COR-morfologi (artikel + bestemt) for relevante slots. */
export function syncMorphologyForSlots(
  slotValues: Record<string, string>,
  script: StoryScript,
  onUpdate: (patch: Record<string, StorySlotMorph>) => void,
): void {
  const ids = new Set<string>(slotIdsWithDefiniteReuse(script));
  for (const beat of script.beats) {
    const artRe = /\{\{art:(\w+)\}\}/g;
    let m: RegExpExecArray | null;
    while ((m = artRe.exec(beat.template)) !== null) {
      ids.add(m[1]!);
    }
  }

  for (const id of ids) {
    const raw = slotValues[id]?.trim();
    if (!raw) continue;
    void lookupNounMorphDa(raw).then((morph) => {
      onUpdate({
        [id]: {
          article: morph.indefiniteArticle,
          definite: morph.definiteSingular,
        },
      });
    });
  }
}

/** Seneste literal-segment før segmentIndex (til gentag på hul). */
export function lastLiteralSpeechBefore(
  segments: StorySegment[],
  beforeIndex: number,
  slotValues: Record<string, string>,
  script: StoryScript,
  slotMorph?: Record<string, StorySlotMorph>,
): string {
  for (let i = beforeIndex - 1; i >= 0; i--) {
    const seg = segments[i];
    if (seg?.kind === 'literal') {
      const speech = segmentLiteralSpeech(
        seg.text,
        i,
        segments,
        slotValues,
        script,
        slotMorph,
      );
      if (speech) return speech;
    }
  }
  return '';
}

/** Alle pladsholder-id'er i en skabelon. */
export function templateSlotIds(template: string): string[] {
  const ids: string[] = [];
  let m: RegExpExecArray | null;
  const re = /\{\{(\w+)(?::(\d+))?\}\}/g;
  while ((m = re.exec(template)) !== null) {
    ids.push(m[1]!);
  }
  ART_PLACEHOLDER_RE.lastIndex = 0;
  SLOT_PLACEHOLDER_RE.lastIndex = 0;
  return ids;
}

export function beatSlotsFilled(
  template: string,
  values: Record<string, string>,
): boolean {
  return templateSlotIds(template).every((id) => id in values);
}

/** Fuld sætning når alle slots er udfyldt. */
export function resolveStoryTemplate(
  template: string,
  values: Record<string, string>,
  script?: StoryScript,
  slotMorph?: Record<string, StorySlotMorph>,
): string {
  let out = '';
  let last = 0;

  for (const token of templateTokens(template)) {
    out += template.slice(last, token.index);
    if (token.kind === 'art') {
      if (!script) {
        out += 'en';
      } else {
        out += articleInsertionText(token.forSlotId, values, slotMorph);
      }
    } else if (token.kind === 'pron') {
      out += pronounInsertionText(token.forSlotId, values);
    } else {
      const v = values[token.id];
      if (v === undefined) {
        throw new Error(
          `resolveStoryTemplate: mangler værdi for {{${token.id}}}`,
        );
      }
      const isReuse = token.maxLen === undefined;
      out += script
        ? slotInsertionText(
            token.id,
            values,
            script,
            slotMorph,
            isReuse,
          )
        : v;
    }
    last = token.index + token.length;
  }

  out += template.slice(last);
  return out;
}

/** Tekst der skal oplæses for et narrate-trin (inkl. brugerord efter hul). */
export function narrateSpeechText(
  narrateText: string,
  slotValues: Record<string, string>,
  script: StoryScript,
  previousBlankId?: string,
  slotMorph?: Record<string, StorySlotMorph>,
): string {
  const trimmed = narrateText.trim();
  if (!trimmed) return narrateText;

  if (previousBlankId && slotValues[previousBlankId]) {
    const word = slotInsertionText(
      previousBlankId,
      slotValues,
      script,
      slotMorph,
      true,
    );
    if (/^sagde\s*$/i.test(trimmed)) {
      return `sagde ${word}`;
    }
    if (trimmed.startsWith('sagde ')) {
      return trimmed;
    }
    return `${word} ${trimmed}`;
  }

  return narrateText;
}

/** Oplæsning for hele linjen — tom streng hvis slots mangler. */
export function beatSpeakText(
  template: string,
  slotValues: Record<string, string>,
  script?: StoryScript,
  slotMorph?: Record<string, StorySlotMorph>,
): string {
  if (!beatSlotsFilled(template, slotValues)) return '';
  return resolveStoryTemplate(template, slotValues, script, slotMorph).trim();
}

/** Er beat kun lyt (ingen skrivning)? */
export function isListenOnlyBeat(beat: StoryBeat): boolean {
  if (beat.listenOnly === true) return true;
  return false;
}

/** Kun de faste tekststykker (til tastatur-validering). */
export function storyFixedLiterals(script: StoryScript): string[] {
  const literals = new Set<string>();
  for (const beat of script.beats) {
    let last = 0;
    for (const token of templateTokens(beat.template)) {
      if (token.index > last) {
        literals.add(beat.template.slice(last, token.index));
      }
      last = token.index + token.length;
    }
    if (last < beat.template.length) literals.add(beat.template.slice(last));
  }
  return [...literals].filter((s) => s.length > 0);
}

export function isStoryEligibleForUser(
  script: StoryScript,
  user: UserProfile,
): boolean {
  const keys = getCumulativeUnlockedKeys(user);
  return storyFixedLiterals(script).every((t) => textUsesOnlyKeys(t, keys));
}

export function storiesForUser(user: UserProfile): StoryScript[] {
  return STORY_SCRIPTS.filter((s) => isStoryEligibleForUser(s, user));
}

export function pickStoryForUser(user: UserProfile): StoryScript | null {
  const pool = storiesForUser(user);
  if (pool.length === 0) return null;

  const lastId = user.preferences?.lastStoryId;
  const candidates =
    pool.length > 1 && lastId
      ? pool.filter((s) => s.id !== lastId)
      : pool;
  const pickFrom = candidates.length > 0 ? candidates : pool;

  const idx = Math.floor(Math.random() * pickFrom.length);
  return pickFrom[idx] ?? null;
}

export function wordCount(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

export { getStoryScript };
